<template>
  <div
    class="popover-group popover-inline"
    @click="groupClicked"
    @mouseenter="groupMouseenter"
  >
    <slot v-if="!hideTargetButton" name="target-button" :slot-props="this">
      <button @click.stop="toggleMenu" :disabled="disabled" :class="buttonClass">
        <span v-if="icon" class="icon">{{ icon }}</span>
        <img v-if="img" class="icon" :src="img" :alt="title">
        <span class="title">{{ title }}</span>
      </button>
    </slot>
      
  
    <!-- There was a bug in Vue somewhere wherein this transition greatly confuses the ModalWindow component -->
    <!-- the workaround right now is to use a transition-group here. -->
    <transition name="popover" mode="out-in" @enter="beforeEnter" @before-leave="popoverDidClose">
      <div v-if="showMenu" key="popover-menu" ref="menu" :class="elementClassName">
        <!--class: popover-menu-wrap-->
        <div class="backdrop" />
        <div class="popover-menu" @mouseleave="mouseleave">
          <div class="arrow-border" />
          <template v-if="!showAlternate">
            <slot />
          </template>
          <template v-else>
            <slot name="alternate" />
          </template>
          <div class="popover-control">
            <button v-if="doneLabel" @click.stop="hideMenu">
              {{ doneLabel }}
            </button>
          </div>
        </div>
      </div>
    </transition>
    
    <div v-if="showMenu && showModalCover" ref="modalCover" class="modal-cover popover" @click="hide" @mousemove="modalCoverMousemove" />
  </div>
</template>

<style src="@/assets/popover.scss" lang="scss"/>

<style scoped>
.popover-group button        { display: flex !important; flex-grow: 1;}
.popover-group button img    { width: 32px; display: inline-block; }
.popover-group button .title { display:inline-block; height: 100%; vertical-align: middle; line-height: 210%;}
</style>

<script>
  import eventbus from '@/eventbus.js'
  import { nextTick } from 'vue'
  import popoverSupport from '@/lib/popover-support'
  
  export default {
    
    name: 'PopoverInline',
    isPopover: true,
    isInline: true,
    
    inject: ['canHandleEscape', 'cannotHandleEscape'],

    provide() {
      let self = this;
      
      return {
        popover: self
      }
    },
    
    props: {
      title: {
        type: String,
        default: 'Show'
      },
      
      disabled: {
        type: Boolean,
        default: () => false,
      },
      
      icon: {
        type: String,
        default: undefined,
      },

      img: {
        type: String,
        default: undefined,
      },
      
      buttonClass: {
        type: String,
        required: false,
      },
      
      // No target button, always points its arrow at the parent
      //
      showFromParent: {
        type: Boolean,
        default: false
      },

      // No target button; caller opens the popover with showFromRect().
      //
      virtualTarget: {
        type: Boolean,
        default: false
      },
      
      showModalCover: {
        type: Boolean,
        default: true
      },

      // Popovers in the same named group can be switched by hovering their controls.
      popoverGroup: {
        type: String,
        default: undefined
      },
      
      // Start showing initially
      startOpen: {
        type: Boolean,
        default: false
      },
      
      doneLabel: {
        type: String,
        default: undefined
      },
      
      prefer: {
        type: String,
        default: 'dn',
      },
            
      closeOnMouseLeave: {
        type: Boolean,
        default: false
      },
      
      outside: {
        type: Boolean,
        default: false
      }
      
    },
    
    data() { 
      return {
        showMenu: false,
        direction: 'dn',
        showAlternate: false,
        targetButton: undefined,
        targetRect: undefined,
        targetParent: undefined,
        targetParentEl: undefined,
        escapeHandlerRegistered: false,
        activeGroupPopover: undefined,
      }
    },
    
    computed: {
      isPopover() { return true; },

      hideTargetButton() {
        return this.showFromParent || this.virtualTarget;
      },
      
      elementClassName() {
        return 'popover-menu-wrap ' + this.direction + ' ' + this.$attrs.class
      },
    },
    
    mounted() {
      // console.debug("PopoverStatic: mounted")
      // if (this.startOpen) {
      //   this.show()
      // }
      eventbus.on('viewWillUnmount', this.viewWillUnmount);
      if (this.popoverGroup) {
        eventbus.on('popover', this.popoverGroupEvent);
        eventbus.on('popoverGroupMousemove', this.popoverGroupMousemove);
      }
    },

    unmounted() {
      eventbus.removeListener('viewWillUnmount', this.viewWillUnmount);
      if (this.popoverGroup) {
        eventbus.removeListener('popover', this.popoverGroupEvent);
        eventbus.removeListener('popoverGroupMousemove', this.popoverGroupMousemove);
      }
      this.unregisterEscapeHandler();
      this.cleanupDetachedElements();
    },
    
  
    methods: {
      ...popoverSupport,
      
      groupClicked(e) {
        // Will receive modal cover clicks
        if (e.target.classList.contains('modal-cover'))
          return;

        if (this.showMenu)
          return;
        
        this.toggleMenu(e);
      },

      groupMouseenter(e) {
        if (!this.popoverGroup || !this.activeGroupPopover || this.showMenu || this.disabled) {
          return;
        }

        this.show(e.currentTarget.querySelector('button') || e.currentTarget);
      },

      modalCoverMousemove(e) {
        if (!this.popoverGroup) {
          return;
        }

        const cover = e.currentTarget;
        let target;
        try {
          cover.style.pointerEvents = 'none';
          target = document.elementFromPoint(e.clientX, e.clientY);
        }
        finally {
          cover.style.pointerEvents = '';
        }
        eventbus.emit('popoverGroupMousemove', this.popoverGroup, target);
      },

      popoverGroupMousemove(group, target) {
        if (group !== this.popoverGroup || !target || this.showMenu || this.disabled ||
            !this.$el.contains(target)) {
          return;
        }

        this.show(this.$el.querySelector('button') || this.$el);
      },

      popoverGroupEvent(action, popover) {
        if (!this.popoverGroup || popover?.popoverGroup !== this.popoverGroup) {
          return;
        }

        if (action === 'willOpen') {
          this.activeGroupPopover = popover;
          if (popover !== this && this.showMenu) {
            this.hide();
          }
        }
        else if (action === 'close' && this.activeGroupPopover === popover) {
          this.activeGroupPopover = undefined;
        }
      },
      
      toggleMenu(e){
        e && e.preventDefault();

        // Dispatch to hide or close
        if (this.showMenu) {
          this.hide(e);
        }
        else{
          this.show(e);
        }
      },
      
      
      show(e) {
        this.showAlternate = e.altKey;
        this.targetRect = undefined;
        this.targetParent = this.$parent;
        this.targetParentEl = this.$parent?.$el || this.$el?.parentElement;
        this.showMenu = true
        
        // Find lowest appropriate targetElm
        //
        if (this.showFromParent) {
          this.targetButton = this.$parent.$el
        }
        else
        if (e.nodeName) {
          this.targetButton = e;
        }
        else {
          this.targetButton = e.currentTarget || e.target;
        }
              
        // Hook to hide other popovers
        eventbus?.emit('popover', 'willOpen', this);
        
        // Make targetButton relative
        // I'm not sure this is appropriate anymore. The popover-group should be shown
        // regardless of shown/hidden state, so the target button will already be positioned
        // relative to the popover-group.
        // 
        /*
        if (!['relative', 'fixed', 'absolute'].includes(this.targetButton.style.position)) {
          // make relative
          this.targetButton.setAttribute('data-qrelative', 'relative');
          this.targetButton.style.position = this.targetButton.style.position;
          this.targetButton.style.zIndex = 200;
        }
        */
                
        this.registerEscapeHandler();
        
        window.addEventListener('scroll', this.reposition);
        window.addEventListener('resize', this.reposition);
                
        nextTick(() => {
          // Make modalCover a descendent of the body so that it covers everything.
          const modalCover = this.$refs.modalCover;
          if (modalCover) document.body.appendChild(modalCover);
        })
      },

      showFromRect(targetRect, opts = {}) {
        if (!targetRect) return;

        this.showAlternate = !!opts.altKey;
        this.targetButton = undefined;
        //this.targetParent = opts.parent || targetRect.parent || this.$parent;

        this.targetParent = opts.parent?.$el ? opts.parent : this.$parent;
        this.targetParentEl = opts.parent?.$el ||
          opts.parent ||
          targetRect.parent?.$el ||
          targetRect.parent ||
          this.$parent?.$el ||
          this.$el?.parentElement;

        this.targetRect = {
          left: targetRect.left,
          top: targetRect.top,
          width: targetRect.width,
          height: targetRect.height,
        };

        eventbus?.emit('popover', 'willOpen', this);

        this.showMenu = true;
        this.registerEscapeHandler();

        window.addEventListener('scroll', this.reposition);
        window.addEventListener('resize', this.reposition);

        nextTick(() => {
          const modalCover = this.$refs.modalCover;
          if (modalCover) document.body.appendChild(modalCover);
        });
      },
      
      hide(opts = {}) {
        // hiding
        window.removeEventListener('scroll', this.reposition);
        window.removeEventListener('resize', this.reposition);
        
        var oldPositionVal;
        if (this.targetButton && (oldPositionVal = this.targetButton.getAttribute('data-qrelative'))) {
          // reset position
          this.targetButton.style.position = oldPositionVal;
        }

        this.targetButton = undefined;
        this.targetRect = undefined;
        this.targetParent = undefined;
        this.targetParentEl = undefined;
        this.showMenu = false;
        this.unregisterEscapeHandler();

        if (opts.immediate) {
          this.cleanupDetachedElements();
          eventbus?.emit('popover', 'close', this);
        }
      },

      viewWillUnmount(possibleParent) {
        if (!this.showMenu || possibleParent === this) return;

        // possibleParent is closing. Check if this popover is
        // mounted in a descendent. We try two strategies:
        // iterate the DOM, and iterate the component heirarchy.
        //
        const possibleParentEl = possibleParent?.$el;
        if (possibleParentEl?.contains &&
           (possibleParentEl === this.targetParentEl ||
            possibleParentEl.contains(this.$el))) {
          this.hide({ immediate: true });
          return;
        }

        const trackedParent = this.targetParent || this;
        let vm = trackedParent;
        while (vm) {
          if (vm === possibleParent) {
            this.hide({ immediate: true });
            break;
          }
          vm = vm.$parent;
        }
      },

      escapePressed(e) {
        if (!this.showMenu) return false;
        this.hide();
        return true;
      },

      registerEscapeHandler() {
        if (this.escapeHandlerRegistered) return;
        this.canHandleEscape(this);
        this.escapeHandlerRegistered = true;
      },

      unregisterEscapeHandler() {
        if (!this.escapeHandlerRegistered) return;
        this.cannotHandleEscape(this);
        this.escapeHandlerRegistered = false;
      },

      cleanupDetachedElements() {
        this.$refs.menu?.remove();
        this.$refs.modalCover?.remove();
      },

      mouseleave(e){
        if (this.closeOnMouseLeave && e.target == e.currentTarget) {
          this.hide(e);
        }
      },
      
      beforeEnter(menu) {
        nextTick(() => {
          document.body.appendChild(menu);
          const fromRect = this.getTargetRect();
          if (!fromRect) return;
          this.direction = this.position(menu, fromRect, {
            prefer: this.prefer,
            outside: this.outside
          });
          
          eventbus?.emit('popover', 'open', this);
        })
      },
    
      popoverDidClose(arg) {
        window.removeEventListener('scroll', this.reposition);
        window.removeEventListener('resize', this.reposition);
        eventbus?.emit('popover', 'close', this);
      },
      
      reposition() {
        const fromRect = this.getTargetRect();
        if (!fromRect || !this.$refs.menu) return;
        this.direction = this.position(this.$refs.menu, fromRect, {
          prefer: this.prefer,
          outside: this.outside
        });
      },

      getTargetRect() {
        if (this.targetRect) {
          return this.targetRect;
        }

        return this.targetButton?.getBoundingClientRect();
      }
      
    }
  }
</script>
